import React, { useMemo } from 'react';
import { ErrorBoundary } from '@toss/error-boundary';
import { Button, ICON_COLOR } from '@zep-us/zus';
import { useTranslation } from 'next-i18next';
import { ErrorBoundaryFallback } from '@/components';
import { useLocale } from '@/hooks';
import { ChangeIcon, MapFillIcon } from '@/icons';
import { useUserPlanManager } from '@/shared/plan';
import { type PlanType } from '@/types';
import { type MapTemplateType, MapTemplateTypeLocaleKey } from '@/types/MapTemplate';
import { cn } from '@/utils';
import { useBuildInfoContext } from '@/views/build/context';
import { useBuildFormContext } from '@/views/build/form/schema';
import { useShowMapTemplateModal } from '../../features/map-selection';
import { resolveMapTemplateAvailability } from '../../utils/resolveMapTemplateAvailability';
import { useFocusTarget } from '../focus/useFocusTarget';
import { MapRecoveryNotice } from './MapRecoveryNotice';
import { ViewportResponsiveButton } from './ViewportResponsiveButton';
type SelectedMapTemplate = {
  thumbnailImage?: string;
  type?: MapTemplateType;
  name?: string;
  serializedLocalize?: Partial<Record<string, {
    name: string;
  }>>;
};
export const SelectMapTemplateField = () => {
  const {
    canEditQuiz,
    mapTemplates,
    originQuizSet
  } = useBuildInfoContext();
  const {
    userPlanManager
  } = useUserPlanManager();
  const {
    open
  } = useShowMapTemplateModal();
  const {
    watch
  } = useBuildFormContext();
  const selectedMapTemplateId = watch('templateId');
  const mapTemplateAvailability = useMemo(() => resolveMapTemplateAvailability({
    templateId: selectedMapTemplateId,
    visibleMapTemplates: mapTemplates,
    originMapTemplate: originQuizSet,
    hasEnoughPlanType: planType => userPlanManager.hasEnoughPlanType(planType as PlanType)
  }), [mapTemplates, originQuizSet, selectedMapTemplateId, userPlanManager]);
  const selectedMap = useMemo(() => {
    const visibleMap = mapTemplates.find(mapTemplate => mapTemplate.templateId === selectedMapTemplateId);
    if (visibleMap) {
      return {
        thumbnailImage: visibleMap.thumbnailImage,
        type: visibleMap.type,
        name: visibleMap.name,
        serializedLocalize: visibleMap.serializedLocalize
      };
    }
    if (mapTemplateAvailability !== 'available-original' && mapTemplateAvailability !== 'unavailable-original') {
      return undefined;
    }
    return {
      thumbnailImage: originQuizSet?.templateImageUrl,
      type: originQuizSet?.templateType as MapTemplateType,
      name: originQuizSet?.templateName,
      serializedLocalize: Object.fromEntries(Object.entries(originQuizSet?.localeTemplateName ?? {}).map(([locale, name]) => [locale, {
        name
      }]))
    };
  }, [mapTemplateAvailability, mapTemplates, originQuizSet, selectedMapTemplateId]);
  const isUnavailableMap = mapTemplateAvailability === 'unavailable-visible' || mapTemplateAvailability === 'unavailable-original';
  const openMapTemplateModal = (isChange: boolean, focusFirstFreeMap = false) => open({
    from: 'settings_page',
    isChange,
    focusFirstFreeMap
  });
  return <>
      <div className="flex w-full flex-col gap-xs">
        {canEditQuiz && isUnavailableMap && <MapRecoveryNotice onChangeMap={() => openMapTemplateModal(true, true)} />}
        {!selectedMap ? <NotSelected handleMapTemplateModalShow={() => openMapTemplateModal(false)} isChangeEnabled={canEditQuiz} /> : <ErrorBoundary renderFallback={e => <ErrorBoundaryFallback {...e} />}>
            <Selected selectedMap={selectedMap} handleMapTemplateModalShow={() => openMapTemplateModal(true, false)} isChangeEnabled={canEditQuiz} isUnavailableMap={isUnavailableMap} />
          </ErrorBoundary>}
      </div>
    </>;
};
const NotSelected = (props: {
  handleMapTemplateModalShow: () => void;
  isChangeEnabled: boolean;
}) => {
  const {
    t
  } = useTranslation(['build']);
  const mapTemplateFieldRef = useFocusTarget<HTMLDivElement>('templateId');
  return <div ref={mapTemplateFieldRef} tabIndex={props.isChangeEnabled ? 0 : -1} role="button" aria-disabled={!props.isChangeEnabled} className={cn(['relative overflow-hidden rounded-md bg-strong-alternative', props.isChangeEnabled && 'cursor-pointer hover:bg-violet-100', !props.isChangeEnabled && 'cursor-default opacity-70', 'border border-opacity-assistive', 'h-[160px] w-full', 'flex justify-center pt-[36px]', 'focus:focus-ring'])} onClick={() => {
    if (props.isChangeEnabled) {
      props.handleMapTemplateModalShow();
    }
  }} onKeyDown={e => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (props.isChangeEnabled) {
        props.handleMapTemplateModalShow();
      }
    }
  }} data-sentry-component="NotSelected" data-sentry-source-file="SelectMapTemplateField.tsx">
      <div className="absolute bottom-0 left-0 right-0 h-[54px]">
        <img src="/assets/map_image.webp" alt="Map preview" className="absolute bottom-0 left-1/2 h-full w-auto -translate-x-1/2 object-cover" />
      </div>

      {/* Center content */}
      <div className="flex flex-col items-center justify-center gap-3xs self-start">
        <div className="flex h-lg w-lg items-center justify-center">
          <MapFillIcon size="md" className="text-strong" data-sentry-element="MapFillIcon" data-sentry-source-file="SelectMapTemplateField.tsx" />
        </div>
        <span className="whitespace-nowrap text-heading-sm-bold text-strong">
          {t('build.map.select')}
        </span>
      </div>
    </div>;
};
const Selected = ({
  selectedMap,
  handleMapTemplateModalShow,
  isChangeEnabled,
  isUnavailableMap
}: {
  selectedMap: SelectedMapTemplate;
  handleMapTemplateModalShow: () => void;
  isChangeEnabled: boolean;
  isUnavailableMap: boolean;
}) => {
  const {
    t
  } = useTranslation(['common', 'build']);
  const {
    locale
  } = useLocale();
  const mapTemplateFieldRef = useFocusTarget<HTMLDivElement>('templateId');
  const isRowClickable = isChangeEnabled && !isUnavailableMap;
  return <div ref={mapTemplateFieldRef} {...isRowClickable ? {
    role: 'button' as const,
    tabIndex: 0,
    onClick: handleMapTemplateModalShow,
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleMapTemplateModalShow();
      }
    }
  } :
  // focusField('templateId') 의 programmatic focus 대상이 되도록 유지
  {
    tabIndex: -1
  }} className={cn(isUnavailableMap ? 'h-[76.6px] mobile-lg:h-[109px] overflow-hidden bg-default' : 'group h-[76.6px] mobile-lg:h-[110px] overflow-hidden border-opacity-assistive bg-default rounded-[22px]', isRowClickable && 'cursor-pointer hover:bg-gray-50')} data-sentry-component="Selected" data-sentry-source-file="SelectMapTemplateField.tsx">
      <div className="relative flex h-full items-center gap-sm">
        {/* Map thumbnail */}
        <div className={cn('relative shrink-0 bg-transparent', isUnavailableMap ? 'w-[110px] p-2xs mobile-lg:w-[160px]' : 'h-full border-2 border-transparent p-3xs group-hover:rounded-[22px] group-hover:border-opacity-black-3')}>
          <div className={cn('relative overflow-hidden rounded-md', isUnavailableMap && 'aspect-[88/58]', !isUnavailableMap && 'h-full')}>
            {!isUnavailableMap && <div className={cn('items-center justify-center hidden absolute inset-0 bg-dimmed rounded-md', isChangeEnabled && 'group-hover:flex')}>
                <MapFillIcon size="xl" fill={ICON_COLOR.inverse} />
              </div>}
            <img className={cn('h-full object-cover rounded-md', isUnavailableMap ? 'w-full' : 'w-[99px] mobile-lg:w-[150px]')} src={selectedMap?.thumbnailImage} alt={selectedMap?.serializedLocalize?.[locale]?.name || selectedMap?.name || 'Map thumbnail'} />
          </div>
        </div>

        {/* Map info */}
        <div className="flex min-w-0 flex-1 flex-col justify-center gap-3xs">
          <h3 className="truncate text-heading-sm-bold text-default">
            {selectedMap?.serializedLocalize?.[locale]?.name ?? selectedMap?.serializedLocalize?.en?.name ?? selectedMap?.name ?? ''}
          </h3>
          <p className="text-body-sm-medium text-alternative">
            {selectedMap?.type && t(MapTemplateTypeLocaleKey[selectedMap.type])}
          </p>
        </div>

        {/* Change button */}
        {isChangeEnabled && <div className="flex shrink-0 items-center">
            {isUnavailableMap ? <Button size="sm" variant="outlined" intent="neutral" leftSlot={<ChangeIcon size="xs" fill={ICON_COLOR.neutral} />} onClick={event => {
          event.stopPropagation();
          handleMapTemplateModalShow();
        }}>
                {t('common:global.change.button')}
              </Button> : <ViewportResponsiveButton className="gap-2xs text-body-sm-semibold" leftSlot={<ChangeIcon size="xs" fill={ICON_COLOR.neutral} />}>
                {t('build:build.lockRecovery.changeMapAction')}
              </ViewportResponsiveButton>}
          </div>}
      </div>
    </div>;
};